import { light_offer_extended } from "@/lib/models/light_offer_extended";
import Image from "next/image";
import {
  isCouponNewCoupon,
  isPercentageDiscountCoupon,
  isSpecialCoupon,
  is10PercentOffCoupon,
  isGachaOffer,
  getOfferIsOutOfRange,
  getOfferEndDate,
  getClipEndDate,
  getHalfWidthLength,
  truncateToHalfWidth,
} from "@/lib/util";
import classNames from "classnames";
import Button from "./Button";

import { useRef, useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useGlobalStore } from "@/app/providers/ZustandProvider";
import { usePixelTracking, useCurrentDate } from "@/lib/hooks";

interface OfferProps {
  offer: light_offer_extended;
  onClick: () => void;
  onClickOfferCard: (id: number) => void;
  onClickDummyOfferCard?: () => void;
  isInBasket: boolean;
  isFaked?: boolean;
  isOfferDisabled?: boolean;
  isLoading?: boolean;
  isClipped?: boolean;
  holderRef?: string;
  isUsed?: boolean | number;
  storeId?: number;
}

function Offer({
  offer,
  onClick,
  onClickOfferCard,
  onClickDummyOfferCard,
  isInBasket,
  isFaked,
  isLoading,
  isClipped,
  isOfferDisabled,
  holderRef,
  isUsed,
  storeId,
}: OfferProps) {
  const impressionSentRef = useRef(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const now = useCurrentDate();

  const { memberId } = useGlobalStore((state) => state);

  const { mutate: trackPixel } = usePixelTracking();

  // Debug mode for QA: show offer id when localStorage.debug === "true"
  const [showDebug, setShowDebug] = useState(false);
  useEffect(() => {
    try {
      const value =
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debug") === "true";
      queueMicrotask(() => setShowDebug(value));
    } catch {
      queueMicrotask(() => setShowDebug(false));
    }
  }, []);

  const end = getOfferEndDate(offer, now);
  const endDate = end.getDate();
  const endMonth = end.getMonth() + 1;
  const time = end.getHours() + ":" + String(end.getMinutes()).padStart(2, "0");
  const isOutOfClickablePeriod = getOfferIsOutOfRange(offer, now);
  const clipEnd = getClipEndDate(offer, now);
  const clipEndDate = clipEnd.getDate();
  const clipEndMonth = clipEnd.getMonth() + 1;
  const isPercentageDiscount = isPercentageDiscountCoupon(offer);
  const shouldShowNewLabel = isCouponNewCoupon(offer, now);

  // Set up Intersection Observer to track viewport visibility
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1, // Fire when 10% of the element is visible on screen
        rootMargin: "0px 0px -150px 0px", // Exclude bottom banner area (typically 80-100px)
      },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [offer.id, isVisible]);

  // Effect to handle pixel API when image is visible and loaded.
  // Gacha offers are excluded — they are not tracked via the pixel API.
  useEffect(() => {
    if (
      !impressionSentRef.current &&
      imageLoaded &&
      isVisible &&
      !isUsed &&
      !isGachaOffer(offer)
    ) {
      impressionSentRef.current = true;
      const retailerId = Number(process.env.NEXT_PUBLIC_RETAILER_ID) || 0;
      trackPixel({
        offer,
        memberId: memberId ?? undefined,
        holderRef: holderRef ?? undefined,
        storeId,
        retailerId,
      });
    }
  }, [
    isUsed,
    isVisible,
    imageLoaded,
    offer.id,
    memberId,
    trackPixel,
    offer.ended_at,
    offer,
    holderRef,
    storeId,
  ]);

  // Handler for image load event
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const isGacha = isGachaOffer(offer);
  // Welcia special day coupons show a text instead of the clip button while
  // outside their clippable period (US#662041). 10% off coupons are unchanged.
  const isSpecialDayOutOfRange =
    !isUsed && isSpecialCoupon(offer) && isOutOfClickablePeriod;
  const label = isGacha
    ? offer.keep === 1
      ? "保存済"
      : "保存"
    : isOfferDisabled
      ? "保存"
      : isInBasket
        ? "保存済"
        : "保存";

  const imageSrc = isUsed
    ? offer.picture_url
    : isSpecialCoupon(offer)
      ? "/images/special-day.jpg"
      : is10PercentOffCoupon(offer)
        ? "/images/10PercentOffCoupon.jpg"
        : isOutOfClickablePeriod
          ? offer.unavailable_clip_image_url
          : offer.picture_url;

  return (
    <div
      ref={containerRef}
      data-testid="offer-card"
      data-offer-id={offer.id}
      data-clipped={Boolean(isClipped)}
      data-in-basket={Boolean(isInBasket)}
      data-used={Boolean(isUsed)}
      data-disabled={Boolean(isOfferDisabled)}
      data-faked={Boolean(isFaked)}
      className={classNames(
        "offer-card p-1.25 gap-0.5 min-[375px]:p-1.5 min-[375px]:gap-1 border border-none rounded-sm bg-white flex items-center justify-between cursor-pointer h-full relative  min-w-26.25",
        "flex-col",
      )}
    >
      {showDebug && !isFaked && (
        <div
          data-testid="offer-card-debug"
          className="absolute top-1 left-1 z-10 flex flex-col gap-0.5 rounded bg-black/80 text-white px-2 py-1 text-xs font-mono"
          aria-label={`Offer ID: ${offer.id}`}
        >
          <span>ID: {offer.id}</span>
          {(() => {
            if (isSpecialCoupon(offer)) return <span>Sp</span>;
            if (is10PercentOffCoupon(offer)) return <span>10%</span>;
            if (isGachaOffer(offer)) return <span>Gacha</span>;
            return null;
          })()}
        </div>
      )}
      {isFaked ? (
        <div
          data-testid="offer-card-placeholder"
          className="offer-content w-full"
        >
          <div
            data-testid="offer-card-member-overlay"
            className="offer-overlay"
            onClick={onClickDummyOfferCard}
          >
            <div className="offer-overlay-content absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center z-20 bg-white/70">
              <div className="offer-overlay-text flex flex-col">
                <span
                  data-testid="offer-card-member-overlay-title"
                  className="font-bold text-ng-font-3 text-[16px] text-center"
                >
                  ウエルシア <br />
                  メンバー限定
                </span>
                <span
                  data-testid="offer-card-member-overlay-cta"
                  className="text-sm bg-ng-font-3 text-white rounded-3xl px-1 py-0.5 mt-2"
                >
                  メンバー登録
                </span>
              </div>
            </div>
          </div>
          <div className={classNames("bg-gray-100", "h-20 w-26.25")} />
          <div className="w-full min-h-16.5" />
        </div>
      ) : (
        <>
          {isUsed ? (
            <div
              data-testid="offer-card-used-overlay"
              className="offer-expired-overlay absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center z-20 bg-white/90 pointer-events-none"
            >
              <span
                data-testid="offer-card-used-label"
                className="offer-expired-text text-lg font-bold text-gray-600"
              >
                利用済
              </span>
            </div>
          ) : null}
          {shouldShowNewLabel || offer.is_new ? (
            <Image
              data-testid="offer-card-new-badge"
              src="/icons/new.svg"
              alt="New"
              className="offer-new-badge absolute top-0 right-0 z-10"
              loading="eager"
              width={45}
              height={45}
            />
          ) : null}
          {isUsed ? (
            <Image
              data-testid="offer-card-image"
              onClick={() => onClickOfferCard(offer.id)}
              src={imageSrc || ""}
              alt={offer.title}
              className={classNames(
                "offer-image object-contain cursor-pointer",
                "h-24 w-auto",
              )}
              width={96}
              height={96}
            />
          ) : (
            <LazyLoadImage
              data-testid="offer-card-image"
              onClick={() => onClickOfferCard(offer.id)}
              src={imageSrc || ""}
              alt={offer.title}
              className={classNames(
                "offer-image object-contain cursor-pointer",
                "h-24 w-auto",
              )}
              effect="blur"
              threshold={300}
              onLoad={handleImageLoad}
              afterLoad={handleImageLoad}
            />
          )}

          <div
            data-testid="offer-card-info"
            className="offer-info w-full flex gap-1 flex-col items-center"
          >
            <div
              data-testid="offer-card-discount-value"
              className="offer-discount-value text-center text-ng-main font-bold text-sm w-full break-words px-1"
            >
              {isGachaOffer(offer)
                ? (offer.subtitle?.length ?? 0) > 6
                  ? offer.subtitle?.slice(0, 6)
                  : (offer.subtitle ?? "")
                : `${offer.discount_value}${isPercentageDiscount ? "%引き" : "円引き"}`}
            </div>
            {isGachaOffer(offer) ? (
              <div
                data-testid="offer-card-metadata"
                className="offer-metadata offer-metadata text-xs text-[10px] text-ng-font-1 w-full flex justify-center"
              >
                <span
                  data-testid="offer-card-validity-date"
                  className="offer-validity-date font-medium"
                >
                  {endMonth}/{endDate} {time}まで
                </span>
              </div>
            ) : (
              <div
                data-testid="offer-card-metadata"
                className="offer-metadata offer-metadata text-xs text-[10px] text-ng-font-1 w-full flex justify-between"
              >
                <span
                  data-testid="offer-card-usage-limit"
                  className="offer-usage-limit whitespace-nowrap font-medium "
                >
                  {offer.subtitle && getHalfWidthLength(offer.subtitle) > 8
                    ? `${truncateToHalfWidth(offer.subtitle, 7)}...`
                    : offer.subtitle}
                </span>
                {isUsed ? (
                  <span
                    data-testid="offer-card-validity-date"
                    className="offer-validity-date text-nowrap font-medium"
                  >
                    {clipEndMonth}/{clipEndDate}まで
                  </span>
                ) : !isOutOfClickablePeriod ? (
                  <span
                    data-testid="offer-card-validity-date"
                    className="offer-validity-date text-nowrap font-medium "
                  >
                    {endMonth}/{endDate}まで
                  </span>
                ) : null}
              </div>
            )}

            {isUsed ? (
              <Button
                className={"rounded-3xl mt-1 w-full text-xs max-h-5 border! "}
                label={"保存済"}
                testId="offer-card-clip-button"
                icon={
                  <Image
                    src={"/icons/hand.png"}
                    alt="Hand"
                    width={11}
                    height={13}
                    className="max-h-3 min-w-3 mr-2 object-contain"
                  />
                }
                variant={"outlined"}
              />
            ) : isSpecialDayOutOfRange ? (
              <span
                data-testid="offer-card-out-of-range-label"
                className="offer-out-of-range-label mt-1 w-full h-5 flex items-center justify-center text-xs font-bold text-ng-red"
              >
                利用期間外
              </span>
            ) : isOfferDisabled ? (
              <Button
                onClick={() => onClick()}
                className={"rounded-3xl mt-1 w-full text-xs max-h-5 border! "}
                label={label}
                disabled={isOfferDisabled}
                testId="offer-card-clip-button"
                icon={
                  <Image
                    src={"/icons/hand-gray.png"}
                    alt="Hand"
                    width={11}
                    height={13}
                    className=" max-h-3 min-w-3 mr-2 object-contain"
                  />
                }
                variant={"outlined"}
              />
            ) : (
              <Button
                onClick={() => onClick()}
                className={"rounded-3xl mt-1 w-full text-xs max-h-5 border! "}
                label={label}
                testId="offer-card-clip-button"
                icon={
                  isLoading ? (
                    <div className="border-ng-main border-t-transparent rounded-full animate-spin w-4 h-4 border-2 " />
                  ) : (
                    <Image
                      src={
                        isClipped ? "/icons/hand-gray.png" : "/icons/hand.png"
                      }
                      alt="Hand"
                      width={11}
                      height={13}
                      className=" max-h-3 min-w-3 mr-2 object-contain"
                    />
                  )
                }
                trailingIcon={
                  isClipped ? (
                    <Image
                      src="/icons/x.png"
                      alt="Remove"
                      width={8}
                      height={8}
                      className="h-3 w-3 ml-2"
                    />
                  ) : undefined
                }
                variant={isClipped ? "clipped" : "outlined"}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Offer;
